import { Link } from "@inertiajs/react"
import { LegalPage } from "@/components/LegalPage"

type Props = { last_updated: string; contact_email: string }

const title = "Data Deletion"
const description =
  "How to ask the Australian Music Database to delete your account and personal information, what gets removed, and how long it takes."

export default function DataDeletion({ last_updated, contact_email }: Props) {
  return (
    <LegalPage title={title} description={description} lastUpdated={last_updated}>
      <section>
        <p>
          You can ask us to delete your account and the personal information attached to it at any time.
          This page explains how, and exactly what does and does not get removed.
        </p>
      </section>

      <section>
        <h2>How to request deletion</h2>
        <p>
          Email <a href={`mailto:${contact_email}`}>{contact_email}</a> from the address on your account,
          with "Delete my data" as the subject. If you can't email from that address, tell us the account
          name and email you signed up with so we can verify the request.
        </p>
        <p>
          There is no automated self-service deletion button — requests are handled by hand, by the
          site's administrator. We will confirm by email once it is done, normally within a few days and
          in any case within 30 days.
        </p>
      </section>

      <section>
        <h2>What gets deleted</h2>
        <ul>
          <li>your account, name and email address;</li>
          <li>your password hash and any active sessions;</li>
          <li>your trivia quiz scores and leaderboard entry;</li>
          <li>your name on the Wall of Fame, if it appears there;</li>
          <li>your attribution on any records you contributed.</li>
        </ul>
      </section>

      <section>
        <h2>What stays</h2>
        <p>Two things survive deletion, and it's only fair to be upfront about them:</p>
        <ul>
          <li>
            <strong>The historical records themselves.</strong> If you added a band, a gig or a chart
            entry, the factual record stays in the database — your <em>attribution</em> is removed, but
            the research isn't. The archive would develop holes otherwise, and the information is about
            public events rather than about you.
          </li>
          <li>
            <strong>Donation records.</strong> Where we have a legal obligation to retain financial
            records for tax purposes, the transaction record is kept — reduced to the minimum needed,
            with your name removed from anything public. Payment records also exist with Stripe, under
            Stripe's own retention rules.
          </li>
        </ul>
        <p>
          If you want a specific record you contributed removed as well, say so in your email and we will
          consider it case by case.
        </p>
      </section>

      <section>
        <h2>Facebook and other logins</h2>
        <p>
          If you arrived here from a link on our Facebook page, note that this site does not use Facebook
          Login and holds no data from your Facebook account. Deleting data held by Facebook itself is
          done through Facebook's own settings, not here.
        </p>
      </section>

      <section>
        <h2>Related</h2>
        <p>
          See our <Link href="/privacy">Privacy Policy</Link> for what we collect and why, and our{" "}
          <Link href="/terms">Terms of Use</Link> for how contributions are handled.
        </p>
      </section>
    </LegalPage>
  )
}
